import { create } from 'zustand'

/**
 * How loudly you hear each other participant, as a personal mix.
 *
 * The participant menu has had a volume slider since it was designed, and it
 * was decoration: a `<Slider/>` with no value, no handler and no participant
 * behind it. There was nowhere for it to write, because no per-peer gain
 * existed anywhere in the app — the only volume control was the OS one, which
 * moves everyone at once.
 *
 * Local to this client and to this session. Nothing is sent to the room: a
 * participant turning someone down is not moderation, and the person being
 * turned down is not told. Deliberately not persisted either — the key is an
 * SFU `peerId`, which is issued per join, so a stored value would either miss
 * its owner on the next call or land on a stranger who happened to be handed
 * the same id.
 */
type ParticipantVolumeState = {
  /** peerId -> gain in 0..1. Absent means full volume, so silence is opt-in. */
  volumes: Record<string, number>
  volumeOf: (peerId: string) => number
  setVolume: (peerId: string, volume: number) => void
  /** Drop everyone. Called on leave so the next meeting starts level. */
  reset: () => void
}

export const DEFAULT_PARTICIPANT_VOLUME = 1

export const useParticipantVolumeStore = create<ParticipantVolumeState>()((set, get) => ({
  volumes: {},
  volumeOf: (peerId) => get().volumes[peerId] ?? DEFAULT_PARTICIPANT_VOLUME,
  setVolume: (peerId, volume) =>
    set((prev) => ({
      volumes: {
        ...prev.volumes,
        // Clamped rather than trusted: the value comes from a slider today, but
        // an out-of-range gain on an audio element throws rather than saturating.
        [peerId]: Math.min(1, Math.max(0, volume)),
      },
    })),
  reset: () => set({ volumes: {} }),
}))
